import { useState } from 'react'
import { setMyPin } from '../lib/api'
import {
  adminAddEmployee,
  adminReissueLink,
  adminSetPin,
  adminSetRole,
  adminUpdateEmployee,
  personalLink,
  roleOf,
  type AdminEmployee,
  type AdminTask,
  type Role,
} from '../lib/adminApi'
import { formatDate, relativeDue } from '../lib/grouping'
import HeaderAction from './HeaderAction'

interface Props {
  token: string
  people: AdminEmployee[]
  /**
   * Already loaded for the Tasks list. Reused here so tapping a person's open
   * count shows exactly what they have on — the Tasks list answers "what is
   * outstanding in the shop", this answers "what has Nathan got".
   */
  tasks: AdminTask[]
  meId: string | null
  onChanged: () => void
  onProblem: (message: string | null) => void
}

interface Draft {
  fullName: string
  email: string
  slackUserId: string
  active: boolean
  role: Role
}

const EMPTY: Draft = { fullName: '', email: '', slackUserId: '', active: true, role: 'staff' }

const ROLES: { value: Role; label: string; hint: string }[] = [
  {
    value: 'staff',
    label: 'Staff',
    hint: 'Signs in by tapping their name. Sees and works on their own tasks.',
  },
  {
    value: 'admin',
    label: 'Admin',
    hint: 'Signs in with a PIN. Creates and assigns tasks, triages issues, manages staff.',
  },
  {
    value: 'super_admin',
    label: 'Super admin',
    hint: 'Everything an admin can do, plus making and removing admins and managing their accounts.',
  },
]

/** Whose PIN form is open. `promoted` means they have just been made an admin. */
interface PinTarget {
  employee_id: string
  full_name: string
  promoted?: boolean
}

export default function AdminPeople({
  token,
  people,
  tasks,
  meId,
  onChanged,
  onProblem,
}: Props) {
  const [openFor, setOpenFor] = useState<string | null>(null)

  /**
   * That person's rows across every live task, open work first then by due
   * date. Uses each assignee's OWN status, not the task's overall progress —
   * a task can be half done and still outstanding for this person.
   */
  const tasksFor = (employeeId: string) =>
    tasks
      .filter((t) => !t.archived && t.assignees.some((a) => a.employee_id === employeeId))
      .map((t) => ({
        task: t,
        mine: t.assignees.find((a) => a.employee_id === employeeId)!,
      }))
      .sort((x, y) => {
        const xd = x.mine.status === 'Completed'
        const yd = y.mine.status === 'Completed'
        if (xd !== yd) return xd ? 1 : -1
        return x.task.due_date.localeCompare(y.task.due_date)
      })

  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [pinFor, setPinFor] = useState<PinTarget | null>(null)
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')

  /**
   * Only a super admin may touch another admin's account (0023). The database
   * enforces it; this just keeps the buttons from offering what will be refused.
   */
  const iAmSuper = people.some((p) => p.employee_id === meId && p.is_super_admin)
  const canManage = (p: AdminEmployee) => p.employee_id === meId || !p.is_admin || iAmSuper

  const savePin = async () => {
    if (!pinFor) return
    setBusy(true)
    onProblem(null)
    try {
      // Your own PIN goes through set_my_pin (token-scoped, cannot touch anyone
      // else). Resetting another admin's goes through the admin path.
      if (pinFor.employee_id === meId) {
        await setMyPin(token, pin)
      } else {
        await adminSetPin(token, pinFor.employee_id, pin)
      }
      setPinFor(null)
      setPin('')
      setPinConfirm('')
      onChanged()
      onProblem(
        pinFor.employee_id === meId
          ? 'Your PIN has been changed. It applies next time you sign in.'
          : pinFor.promoted
            ? `PIN set for ${pinFor.full_name}. Tell them the number — they sign in by picking their name and entering it. Their old link no longer works; use Copy link on their card for the new one.`
            : `PIN set for ${pinFor.full_name}. Tell them the new number.`,
      )
    } catch (err) {
      onProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const copyLink = async (person: AdminEmployee) => {
    if (!person.link_token) return
    const link = personalLink(person.link_token)
    try {
      await navigator.clipboard.writeText(link)
      setCopied(person.employee_id)
      window.setTimeout(() => setCopied(null), 2000)
    } catch {
      // Clipboard access can be refused; showing the link is a usable fallback.
      onProblem(`Copy failed. Their link is: ${link}`)
    }
  }

  /**
   * Reissue a link and put the new one on the clipboard in a single tap.
   *
   * NOT async at the top level, deliberately. Safari only honours a clipboard
   * write that is started during the tap itself; anything after an `await` has
   * lost the user gesture and is refused. So the ClipboardItem is constructed
   * synchronously here, wrapping a *promise* of the text — which Safari
   * supports precisely for this case. `writeText` after the await is kept as a
   * fallback for browsers without ClipboardItem, and showing the link on screen
   * is the last resort so a tap never leaves you empty-handed.
   */
  const reissue = (person: AdminEmployee) => {
    setBusy(true)
    onProblem(null)

    const linkPromise = adminReissueLink(token, person.employee_id).then((t) =>
      personalLink(t),
    )

    let gestureCopy: Promise<void> | null = null
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      try {
        gestureCopy = navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': linkPromise.then(
              (l) => new Blob([l], { type: 'text/plain' }),
            ),
          }),
        ])
      } catch {
        gestureCopy = null
      }
    }

    void (async () => {
      try {
        const link = await linkPromise
        let onClipboard = false

        if (gestureCopy) {
          try {
            await gestureCopy
            onClipboard = true
          } catch {
            /* fall through to writeText */
          }
        }
        if (!onClipboard) {
          try {
            await navigator.clipboard.writeText(link)
            onClipboard = true
          } catch {
            /* fall through to showing it */
          }
        }

        onChanged()
        if (onClipboard) {
          setCopied(person.employee_id)
          window.setTimeout(() => setCopied(null), 2500)
          onProblem(
            `New link for ${person.full_name} copied to your clipboard. Their old link no longer works — paste this one to them.`,
          )
        } else {
          onProblem(
            `New link for ${person.full_name} (their old one no longer works). Copy it by hand: ${link}`,
          )
        }
      } catch (err) {
        onProblem(err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(false)
      }
    })()
  }

  /** The role of the person being edited as saved now; a new person is staff. */
  const roleBefore = (): Role => {
    const existing = people.find((p) => p.employee_id === editing)
    return existing ? roleOf(existing) : 'staff'
  }

  const save = async () => {
    setBusy(true)
    onProblem(null)
    try {
      let id: string | null = null
      const before = roleBefore()
      if (editing === 'new') {
        // New people always start as staff; a role picked here is applied next.
        id = await adminAddEmployee(token, {
          fullName: draft.fullName,
          email: draft.email.trim() === '' ? null : draft.email,
          slackUserId: draft.slackUserId.trim() === '' ? null : draft.slackUserId,
        })
      } else if (editing) {
        id = editing
        await adminUpdateEmployee(token, editing, {
          fullName: draft.fullName,
          email: draft.email.trim() === '' ? null : draft.email,
          slackUserId: draft.slackUserId.trim() === '' ? null : draft.slackUserId,
          active: draft.active,
        })
      }
      setEditing(null)

      // The picker is hidden for someone being deactivated; only active people change role.
      if (id && draft.active && draft.role !== before) {
        // Details are already saved, so a refused role change leaves those in place.
        await adminSetRole(token, id, draft.role)
        const name = draft.fullName.trim()
        if (before === 'staff') {
          // They have just become an admin with no PIN and a new link. Go straight
          // to setting the PIN, or they cannot sign in at all.
          setPinFor({ employee_id: id, full_name: name, promoted: true })
          setPin('')
          setPinConfirm('')
        } else if (draft.role === 'staff') {
          onProblem(`${name} is now staff. Their PIN was cleared — they sign in by tapping their name.`)
        } else {
          onProblem(
            `${name} is now ${draft.role === 'super_admin' ? 'a super admin' : 'an admin'}.`,
          )
        }
      }
      onChanged()
    } catch (err) {
      onChanged()
      onProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (pinFor) {
    const isMe = pinFor.employee_id === meId
    const tooShort = pin.length < 4
    const mismatch = pin !== pinConfirm
    return (
      <section className="form">
        <h2 className="form__title">
          {isMe ? 'Change your PIN' : `Set a PIN for ${pinFor.full_name}`}
        </h2>
        {pinFor.promoted && (
          <p className="form__lead">
            {pinFor.full_name} is now an admin. Admins sign in with their name and a PIN, so
            they cannot sign in until one is set. Their old link has stopped working.
          </p>
        )}
        <p className="muted form__lead">
          4 to 8 digits.{' '}
          {isMe
            ? 'You will use it the next time you sign in on a new device.'
            : 'They will need this to sign in — tell them what you set.'}
        </p>

        <label className="field">
          <span className="field__label">New PIN</span>
          <input
            className="field__input pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={8}
            value={pin}
            autoFocus
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </label>

        <label className="field">
          <span className="field__label">Type it again</span>
          <input
            className="field__input pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={8}
            value={pinConfirm}
            onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ''))}
          />
          {/* The realistic failure here is a typo, which would lock the person
              out of an account only another admin can rescue. Hence confirming. */}
          <span className="field__hint">
            Typing it twice guards against a typo locking {isMe ? 'you' : 'them'} out.
          </span>
        </label>

        <div className="form__actions">
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              setPinFor(null)
              setPin('')
              setPinConfirm('')
            }}
          >
            {pinFor.promoted ? 'Later' : 'Cancel'}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || tooShort || mismatch}
            onClick={() => void savePin()}
          >
            {busy ? 'Saving…' : 'Save PIN'}
          </button>
        </div>
        {(tooShort || mismatch) && (
          <p className="muted form__hint">
            {tooShort ? 'Needs at least 4 digits.' : 'The two PINs do not match.'}
          </p>
        )}
      </section>
    )
  }

  if (editing) {
    return (
      <section className="form">
        <h2 className="form__title">{editing === 'new' ? 'Add someone' : 'Edit person'}</h2>

        <label className="field">
          <span className="field__label">Name</span>
          <input
            className="field__input"
            value={draft.fullName}
            autoFocus
            onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Email (optional)</span>
          <input
            className="field__input"
            type="email"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">
            Slack member ID{' '}
            <span className="muted">— Slack profile → ⋯ → Copy member ID</span>
          </span>
          <input
            className="field__input"
            value={draft.slackUserId}
            placeholder="U01ABC23DEF"
            onChange={(e) => setDraft({ ...draft, slackUserId: e.target.value })}
          />
          {/* This warning is load-bearing: only 6 of the original 11 people had
              the same address in Slack as on the roster, so a guessed ID sends
              reminders to nobody and raises no error. */}
          {/* No email fallback exists (locked decision #5): a blank ID means silence. */}
          <span className="field__hint">
            Copy it from Slack — it cannot be worked out from their email. Without it they
            get no reminders or alerts at all.
          </span>
        </label>

        {editing !== 'new' && (
          <label className="check">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            <span>
              Active <span className="muted">— unticking hides them and stops reminders</span>
            </span>
          </label>
        )}

        {/* Hidden for someone being deactivated: only active people change role. */}
        {iAmSuper && editing !== meId && draft.active && (
          <fieldset className="roles">
            <legend className="field__label">Role</legend>
            {ROLES.map((r) => (
              <label key={r.value} className="check">
                <input
                  type="radio"
                  name="role"
                  value={r.value}
                  checked={draft.role === r.value}
                  onChange={() => setDraft({ ...draft, role: r.value })}
                />
                <span>
                  {r.label}
                  <span className="field__hint">{r.hint}</span>
                </span>
              </label>
            ))}
            {(() => {
              const before = roleBefore()
              if (before === 'staff' && draft.role !== 'staff' && editing !== 'new') {
                return (
                  <span className="field__hint">
                    Their current link will stop working and they will need a PIN — you set it
                    next.
                  </span>
                )
              }
              if (before === 'staff' && draft.role !== 'staff') {
                return <span className="field__hint">You set their PIN next.</span>
              }
              if (before !== 'staff' && draft.role === 'staff') {
                return (
                  <span className="field__hint">
                    Their PIN is cleared and they go back to signing in by tapping their name.
                  </span>
                )
              }
              return null
            })()}
          </fieldset>
        )}

        <div className="form__actions">
          <button type="button" className="btn btn--quiet" onClick={() => setEditing(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || draft.fullName.trim() === ''}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="cards">
      <HeaderAction>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setDraft(EMPTY)
            setEditing('new')
            onProblem(null)
          }}
        >
          + Add someone
        </button>
      </HeaderAction>

      {people.map((p) => (
        <article key={p.employee_id} className={'card' + (p.active ? '' : ' card--done')}>
          <div className="card__head">
            <h3 className="card__title">{p.full_name}</h3>
            <div className="card__badges">
              {p.is_super_admin ? (
                <span className="chip chip--accent">Super admin</span>
              ) : (
                p.is_admin && <span className="chip chip--low">Admin</span>
              )}
              {!p.active && <span className="chip chip--low">Inactive</span>}
            </div>
          </div>

          <p className="card__due">
            {(() => {
              const theirs = tasksFor(p.employee_id)
              const expanded = openFor === p.employee_id
              if (theirs.length === 0) return <span>No tasks</span>
              return (
                <button
                  type="button"
                  className="linkish"
                  aria-expanded={expanded}
                  onClick={() => setOpenFor(expanded ? null : p.employee_id)}
                >
                  {p.open_tasks === 0 ? 'Nothing open' : `${p.open_tasks} open`}
                  {' '}
                  {expanded ? '▲' : '▼'}
                </button>
              )
            })()}
            {/* Trap #2 in CLAUDE.md: everything queued for someone with no Slack
                ID is written and never sent, with no error anywhere. Say so. */}
            {p.slack_user_id ? (
              ' · Slack ✓'
            ) : (
              <>
                {' · '}
                <span className="card__rel--late">no Slack ID — will not get reminders</span>
              </>
            )}
            {p.is_admin && (p.pin_set ? ' · PIN set' : ' · no PIN — cannot sign in')}
          </p>

          {openFor === p.employee_id && (
            <ul className="mini">
              {tasksFor(p.employee_id).map(({ task, mine }) => {
                const done = mine.status === 'Completed'
                const late = !done && task.days_until < 0
                return (
                  <li key={task.task_id} className={'mini__row' + (done ? ' mini__row--done' : '')}>
                    <span className="mini__title">{task.title}</span>
                    <span className="mini__meta">
                      <span className={late ? 'card__rel--late' : undefined}>
                        Due {formatDate(task.due_date)}
                        {!done && ` · ${relativeDue(task.days_until)}`}
                      </span>
                      <span
                        className={
                          'who__chip who__chip--' +
                          (done ? 'done' : mine.status === 'In Progress' ? 'working' : 'todo')
                        }
                      >
                        {mine.status}
                      </span>
                    </span>
                    {mine.note && <span className="mini__note">💬 {mine.note}</span>}
                  </li>
                )
              })}
            </ul>
          )}

          {!canManage(p) ? (
            <p className="muted card__due">Managed by a super admin</p>
          ) : (
            <div className="card__actions">
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => void copyLink(p)}
              >
                {copied === p.employee_id ? 'Copied ✓' : 'Copy link'}
              </button>
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setDraft({
                    fullName: p.full_name,
                    email: p.email ?? '',
                    slackUserId: p.slack_user_id ?? '',
                    active: p.active,
                    role: roleOf(p),
                  })
                  setEditing(p.employee_id)
                  onProblem(null)
                }}
              >
                Edit
              </button>
              {/* Only admins have PINs, so only admins get this button. Your own
                  row says "Change PIN"; another admin's says "Reset PIN". */}
              {p.is_admin && (
                <button
                  type="button"
                  className="btn btn--quiet"
                  onClick={() => {
                    setPinFor(p)
                    setPin('')
                    setPinConfirm('')
                    onProblem(null)
                  }}
                >
                  {p.employee_id === meId
                    ? 'Change PIN'
                    : p.pin_set
                      ? 'Reset PIN'
                      : 'Set PIN'}
                </button>
              )}
              <button
                type="button"
                className="btn btn--quiet"
                disabled={busy || p.employee_id === meId}
                title={
                  p.employee_id === meId
                    ? 'You cannot replace your own link from here'
                    : 'Replaces their link and copies the new one. Kills the old URL, but does NOT remove their access — untick Active for that.'
                }
                onClick={() => reissue(p)}
              >
                {busy ? 'Working…' : 'New link'}
              </button>
            </div>
          )}
        </article>
      ))}
    </section>
  )
}
